import * as React from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { LoginType, UserFragment } from '@websky/graphql';
import { useAuthenticate } from '@features/auth';
import { useTheme } from '../../../../../theme';
import { useConfig } from '../../../../../context';
import { useToggleable } from '../../../../../hooks';
import Fields from '../Fields/Fields';
import Link from '../../../../../Link';
import { Switch } from '@shared/ui';
import { formatError, getCorporateNumber } from '../../../../utils';
import { BaseButton as Button, IconButton } from '../../../../../BaseComponents';
import { useProfileUpdateService } from '../../../../../Account/hooks';

interface Props {
	onLoyaltyCardSaved?: () => Promise<void> | void;
	toCreateAccount?: () => void;
	toResetPassword?: () => void;
	queryType?: 'update' | 'authenticate';
	showRegisterAction?: boolean;
	userProfile?: UserFragment;
}

const Login: React.FC<Props> = props => {
	const { t } = useTranslation('Loyalty');
	const css = useTheme('Loyalty').LoyaltyConnect;

	const updateUserProfile = useProfileUpdateService(props.userProfile);

	const [authenticate] = useAuthenticate();

	const {
		global: {
			companyInfo: { iataCode }
		}
	} = useConfig();

	const isB2 = iataCode === 'B2';

	const { isOpen: isCorporateCard, toggle: onToggleCorporateCard } = useToggleable(false);

	const onSubmit = async (data: any) => {
		// [WEBSKY-5248] workaround for B2, adding a prefix for corporate cards
		const loyaltyNumber = (isB2 && isCorporateCard
			? getCorporateNumber(data['loyaltyNumber'])
			: data['loyaltyNumber']
		).trim();

		if (props.queryType === 'authenticate') {
			try {
				const res = await authenticate({
					variables: {
						params: {
							loginType: LoginType.FFP,
							login: loyaltyNumber,
							secureCode: data['loyaltyPassword']
						}
					}
				});

				if (!!res.data?.Authenticate && !res.errors) {
					await props?.onLoyaltyCardSaved();
				}
			} catch (error) {
				const errorMessage = formatError(error);

				return {
					loyaltyNumber: t(errorMessage),
					loyaltyPassword: errorMessage
				};
			}
		} else {
			try {
				const res = await updateUserProfile({
					loyaltyCards: [
						{
							airlineCode: iataCode,
							cardNumber: loyaltyNumber,
							pinCode: data['loyaltyPassword']
						}
					]
				});

				if (res?.data?.UpdateUserProfile && !res?.errors) {
					await props?.onLoyaltyCardSaved();

					return undefined;
				}
			} catch (error) {
				const errorMessage = formatError(error);

				return {
					loyaltyNumber: t(errorMessage),
					loyaltyPassword: errorMessage
				};
			}
		}

		return undefined;
	};

	return (
		<div className={css.loyaltyConnect}>
			<div className={css.header}>{t('Log in to LightJet Club')}</div>

			<div>
				<Form onSubmit={onSubmit}>
					{({ handleSubmit, submitting, submitSucceeded }) => (
						<form onSubmit={handleSubmit}>
							<div className={css.group}>
								<Fields />
							</div>

							{/* [WEBSKY-5248] workaround for B2 */}
							{isB2 && (
								<div className={cn(css.group, css.group_corporate)}>
									<Switch
										checked={isCorporateCard}
										onChange={onToggleCorporateCard}
										label={t('Passenger:Corporate card')}
										labelPosition="right"
									/>
								</div>
							)}

							<div className={css.buttons__wrapper}>
								<div className={css.actions}>
									{props.showRegisterAction && (
										<Link action={props.toCreateAccount}>
											{t('Not a member yet? Join for free!')}
										</Link>
									)}

									<Link action={props.toResetPassword}>{t('Forgot password?')}</Link>
								</div>

								{submitSucceeded ? (
									<IconButton isSuccess={submitSucceeded} />
								) : (
									<Button variant="action" type="submit" isLoading={submitting}>
										{t('Connect')}
									</Button>
								)}
							</div>
						</form>
					)}
				</Form>
			</div>
		</div>
	);
};

export default Login;
